import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { OnboardingInput, UpdateOrgInput, UpdateOrgSettingsInput } from '@repo/validation';
import type { BusinessType, ClassMode } from '@repo/shared-types';
import { fetchApi } from '../lib/api';
import i18n from '../i18n';
import { useAuthStore } from '../stores/authStore';
import type { AuthMeResponse } from './useAuth';

/**
 * Shape of GET /api/org/settings. The org's own fields are flat on `data`
 * (they are not nested under an `organization` key), and ids are cuid strings.
 */
interface OrgSettingsResponse {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  /** THI-263: `#rrggbb` or `null` for the LopHoc default green. Carries to the
   *  sổ liên lạc band, the install manifest's theme colour and the public
   *  invoice's top band — see `@repo/shared-types/src/branding.ts`. */
  brandColor: string | null;
  bankCode: string | null;
  bankAccountNo: string | null;
  bankAccountName: string | null;
  settings: {
    industry: string | null;
    teamSize: string | null;
    preferredChannel: string | null;
    defaultCurrency: string | null;
    aiPersonalization: unknown | null;
    showTeacherAssignment: boolean;
    sellsExtras: boolean;
    businessType: BusinessType;
  };
  /** Flattened alongside `settings` — defined (defaults to `false`) even for an
   *  org with no settings row yet, since new PER_SESSION enrollments need a
   *  well-defined initial `chargeAbsent`. See POST /auth/onboarding (write) and
   *  POST /enrollments/charge-absent/bulk (re-apply to existing enrollments). */
  defaultChargeAbsent: boolean;
  /** Flattened alongside `settings`, same shape as `defaultChargeAbsent` above:
   *  `null` (no cap) is a real, well-defined value rather than a placeholder for
   *  "not loaded yet". Written by PUT /org/settings, not by onboarding — see
   *  `useUpdateOrgSettings` below. */
  maxExcusedPerMonth: number | null;
  /** The org-editable manual-send message template (see
   *  apps/dashboard/src/lib/invoiceMessage.ts). `null` means "use
   *  `DEFAULT_INVOICE_MESSAGE_TEMPLATE`" — never resolve this field without
   *  that fallback. Written by PUT /org/settings, same partial-update
   *  contract as `maxExcusedPerMonth`. */
  invoiceMessageTemplate: string | null;
  /** The org's own RAW preference for the public-invoice watermark badge
   *  (THI-90); defaults to `true` even with no settings row. This is NOT the
   *  effective on-page value — a Free/trial-expired plan forces it on
   *  regardless, which `BillingRulesSection` derives separately from
   *  `useSubscription()`'s `plan.showsWatermark`, the same source the other
   *  tier gates in settings already read (e.g. `extrasAllowed`). Written by
   *  PUT /org/settings, same partial-update contract as `maxExcusedPerMonth`. */
  showInvoiceWatermark: boolean;
  /** Default invoice due-day (THI-100): day of month 1-28, or `null` when the
   *  org has no default — the +7-days rule (`DEFAULT_PAYMENT_TERM_DAYS`)
   *  stands. Drives the chốt-sổ confirm dialog's collapsed-vs-pre-expanded
   *  "Hạn thanh toán" section — see lib/dueDatePrefill.ts. Written by
   *  `PUT /org/settings`, same partial-update contract as the fields above. */
  invoiceDueDay: number | null;
  /** THI-267: how the org organizes students into classes — defaults to
   *  `INDIVIDUAL` even for an org with no settings row yet (same flattening
   *  as `defaultChargeAbsent` above). Drives ClassesPage's default tab and
   *  the class form's variant; every capability stays reachable under both
   *  values (spec §2 decision 1). Written by `PUT /org/settings` (Cài đặt ›
   *  Hiển thị "Tổ chức lớp" row), same partial-update contract as the
   *  sibling fields. */
  classMode: ClassMode;
}

export function useOrgSettings() {
  return useQuery<OrgSettingsResponse>({
    queryKey: ['org', 'settings'],
    queryFn: () => fetchApi<OrgSettingsResponse>('/org/settings'),
    staleTime: 5 * 60 * 1000,
  });
}

/** THI-267: `classMode`, defaulted the same way the field itself defaults
 *  (`INDIVIDUAL`) while the settings query is pending — ClassesPage's
 *  default-tab pick reads this, and INDIVIDUAL is also the safer guess
 *  (today's one-slot-per-teacher-time behaviour) for the cold-load window. */
export function useClassMode(): ClassMode {
  const { data } = useOrgSettings();
  return data?.classMode ?? 'INDIVIDUAL';
}

/**
 * `POST /auth/onboarding` used as a preferences update. Every field on
 * `onboardingSchema` is optional AND the route's UPDATE branch is partial
 * since 2026-08-22 (see the onboarding handler in `apps/api/src/routes/auth.ts`)
 * — only the fields present in the body are written — so a caller sends just
 * what it changed. That is what lets the settings screen save one section at a
 * time without a display save resetting `defaultChargeAbsent`. The CREATE
 * branch is unchanged: first-time onboarding still fills a brand-new settings
 * row with the defaults for anything the wizard left out.
 *
 * `silent` suppresses the SUCCESS toast only — invalidation and the error
 * toast always run. A settings section fires this alongside
 * `useUpdateOrgSettings` and owns the single "Đã lưu thay đổi" toast for the
 * whole save action (spec §4's double-toast fix); letting this hook toast too
 * is exactly the second toast that fix removes. Anything that saves
 * preferences on its own (and wants the feedback) omits the option.
 * No caller takes the non-silent branch today — it stays the default so a
 * future standalone caller gets feedback without having to opt in.
 */
export function useUpdatePreferences(options?: { silent?: boolean }) {
  const queryClient = useQueryClient();
  const silent = options?.silent ?? false;

  return useMutation({
    mutationFn: (input: OnboardingInput) =>
      fetchApi<{ message: string }>('/auth/onboarding', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'settings'] });
      if (!silent) toast.success(i18n.t('preferences.toastUpdated', { ns: 'settings' }));
    },
    onError: (err: Error) => {
      toast.error(err.message || i18n.t('preferences.toastError', { ns: 'settings' }));
    },
  });
}

/**
 * Excused-absence quota cap only. Deliberately its own mutation and its own
 * route (`PUT /org/settings`) rather than a field on `useUpdatePreferences`:
 * that one POSTs to `/auth/onboarding`, whose `onboardingSchema` doesn't know
 * this field and would silently strip it. Silent on success —
 * `BillingRulesSection` fires this alongside `updatePreferences` and owns the
 * one toast for the whole save — but it still toasts on error so a failed
 * quota save isn't mistaken for success.
 */
export function useUpdateOrgSettings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateOrgSettingsInput) =>
      fetchApi<{
        maxExcusedPerMonth: number | null;
        invoiceMessageTemplate: string | null;
        showInvoiceWatermark: boolean;
        invoiceDueDay: number | null;
      }>(
        '/org/settings',
        {
          method: 'PUT',
          body: JSON.stringify(input),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', 'settings'] });
    },
    onError: (err: Error) => {
      toast.error(err.message || i18n.t('preferences.toastError', { ns: 'settings' }));
    },
  });
}

interface UpdateOrgResponse {
  organization: { id: string; name: string; slug: string; logoUrl: string | null; brandColor: string | null };
}

/**
 * Edit the org name and/or logo (OWNER/ADMIN). Mirrors the result into the
 * auth store because DashboardLayout renders the org name from there — a
 * query-only update would leave the sidebar stale until a reload. Also
 * patches the `['auth','me']` cache so it doesn't overwrite the rename with
 * the stale login-time payload the next time `useAuthMe`'s effect runs (e.g.
 * on the access-token refresh that follows the first 401 after 15 min).
 */
export function useUpdateOrg() {
  const queryClient = useQueryClient();
  const setOrganization = useAuthStore((s) => s.setOrganization);

  return useMutation({
    mutationFn: (input: UpdateOrgInput) =>
      fetchApi<UpdateOrgResponse>('/org', {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: ({ organization }) => {
      setOrganization(organization);
      queryClient.setQueryData<AuthMeResponse>(['auth', 'me'], (old) =>
        old ? { ...old, organization } : old,
      );
      queryClient.invalidateQueries({ queryKey: ['org', 'settings'] });
      toast.success(i18n.t('profile.org.updated', { ns: 'settings' }));
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
